<template>
  <div class="">
    <AppSpinner
      v-if="loading && Object.keys(event).length === 0"
      class="page-spinner"
    />

    <div
      v-else
      class="flex flex-col gap-1"
    >
      <div class="font-medium text-lg flex-col gap-1 mb-8">
        <div class="mb-6 flex gap-4 text-xl">
          <div class="flex flex-col text-black gap-2">
            <span>ID: </span>
            <span>Name:</span>
            <span>Slug:</span>
            <span>Date:</span>
            <span>Location:</span>
            <span>Sponsored:</span>
            <span>Bulk Purchases Enabled:</span>
            <span>Devices:</span>
          </div>

          <div class="flex flex-col gap-2">
            <span>{{ event.id }}</span>
            <span>{{ event.name }}</span>
            <span>{{ event.slug }}</span>
            <span>{{ dateFormatter.formatShortDate(event.date) }}</span>
            <span>{{ event.location }}</span>
            <span>{{ event.isSponsored ? 'Yes' : 'No' }}</span>
            <span>{{ event.canPurchaseBulkPhotos ? 'Yes' : 'No' }}</span>
            <span>{{ event.devices || 'No added devices' }}</span>
          </div>
        </div>

        <!-- Event Image -->
        <AdminCTA
          :event="event"
          :disabled="loading"
          image-type="Event Image"
          :image-to-display="event.eventImage"
          @handle-show-upload="handleShowUploadImage('event-image', 'Event Image', event.eventImage)"
          @handle-show-delete="handleShowImageDeleteModal('event-image', 'Event Image')"
        />

        <!-- Event Background Image -->
        <AdminCTA
          :event="event"
          :disabled="loading"
          image-type="Event Background Image"
          :image-to-display="event.eventBackgroundImage"
          @handle-show-upload="handleShowUploadImage('event-background-image', 'Event Background Image', event.eventBackgroundImage)"
          @handle-show-delete="handleShowImageDeleteModal('event-background-image', 'Event Background Image')"
        />

        <!-- Event Overlay Portrait -->
        <AdminCTA
          :event="event"
          :disabled="loading"
          image-type="Event Overlay Portrait"
          :image-to-display="event.eventOverlayPortrait"
          @handle-show-upload="handleShowUploadImage('event-overlay-portrait', 'Event Overlay Portrait', event.eventOverlayPortrait)"
          @handle-show-delete="handleShowImageDeleteModal('event-overlay-portrait', 'Event Overlay Portrait')"
        />

        <!-- Event Overlay Landscape -->
        <AdminCTA
          :event="event"
          :disabled="loading"
          image-type="Event Overlay Landscape"
          :image-to-display="event.eventOverlayLandscape"
          @handle-show-upload="handleShowUploadImage('event-overlay-landscape', 'Event Overlay Landscape', event.eventOverlayLandscape)"
          @handle-show-delete="handleShowImageDeleteModal('event-overlay-landscape', 'Event Overlay Landscape')"
        />

        <template v-if="event.isSponsored">
          <!-- Sponsor Overlay Portrait -->
          <AdminCTA
            :event="event"
            :disabled="loading"
            image-type="Sponsor Overlay Portrait"
            :image-to-display="event.sponsorOverlayPortrait"
            @handle-show-upload="handleShowUploadImage('sponsor-overlay-portrait', 'Sponsor Overlay Portrait', event.sponsorOverlayPortrait)"
            @handle-show-delete="handleShowImageDeleteModal('sponsor-overlay-portrait', 'Sponsor Overlay Portrait')"
          />
          <!-- Sponsor Overlay Landscape -->
          <AdminCTA
            :event="event"
            :disabled="loading"
            image-type="Sponsor Overlay Landscape"
            :image-to-display="event.sponsorOverlayLandscape"
            @handle-show-upload="handleShowUploadImage('sponsor-overlay-landscape', 'Sponsor Overlay Landscape', event.sponsorOverlayLandscape)"
            @handle-show-delete="handleShowImageDeleteModal('sponsor-overlay-landscape', 'Sponsor Overlay Landscape')"
          />
        </template>

        <div class="flex justify-end mb-6">
          <AppButton
            type="button"
            :disabled="loading"
            class="btn-solid btn-secondary rounded-sm px-3 md:px-5 whitespace-nowrap"
            @click="showAddCategoryModal = true"
          >
            <FontAwesomeIcon :icon="faPlus" />
            Add Category
          </AppButton>
        </div>
      </div>

      <AppTable
        :loading="loading"
        :if-looped-items-exist="event.categories?.length > 0"
        class="max-w-7xl"
      >
        <template #headings>
          <th>Category</th>
          <th>Protected</th>
          <th />
        </template>

        <template #rows>
          <tr
            v-for="category in event.categories"
            :key="category.id"
          >
            <td>{{ category.name }}</td>

            <td>{{ category.protected ? 'Yes' : 'No' }}</td>

            <td>
              <div class="table-dropdown">
                <AppDropdown class="admin-table-dropdown">
                  <AppDropdownItem @click="categoryToEdit = category">
                    Edit
                  </AppDropdownItem>

                  <AppDropdownItem @click="categoryToDelete = category">
                    Delete
                  </AppDropdownItem>
                </AppDropdown>
              </div>
            </td>
          </tr>
        </template>
      </AppTable>
    </div>

    <!--  add a file-->
    <Transition name="fade">
      <UploadEventFileModal
        v-if="showAddImageModal === true"
        :processing="isSubmitting"
        :image-type="imageType"
        :image="image"
        :validation-error="errors"
        @upload="handleUploadImage"
        @close="() => { showAddImageModal = false; imageEndpoint = null; errors = null }"
      />
    </Transition>

    <!--  delete an uploaded file-->
    <Transition name="fade">
      <DeleteModal
        v-if="showEventDeleteImageModel === true"
        :text="'Are you sure you want to delete this ' + imageType.toLowerCase() + '?'"
        :title="imageType"
        :errors="errors"
        :processing="isSubmitting"
        @delete="handleImageDelete"
        @close="() => { imageEndpoint = null; imageType = null; showEventDeleteImageModel = false; errors = null }"
      />
    </Transition>

    <!--  edit category-->
    <Transition name="fade">
      <AppModal
        v-if="categoryToEdit"
        :disabled="isSubmitting"
        class="event-overview-edit-modal"
        @close="() => { categoryToEdit = null; errors = null }"
      >
        <CategoryForm
          :category="categoryToEdit"
          :errors="errors"
          :processing="isSubmitting"
          @confirm="handleEditCategories"
          @close="() => { categoryToEdit = null; errors = null }"
        />
      </AppModal>
    </Transition>

    <!--  add category-->
    <Transition name="fade">
      <AppModal
        v-if="showAddCategoryModal"
        :disabled="isSubmitting"
        class="event-overview-add-modal"
        @close="() => { showAddCategoryModal = null; errors = null }"
      >
        <CategoryForm
          :errors="errors"
          :processing="isSubmitting"
          @confirm="handleAddCategories"
          @close="() => { showAddCategoryModal = null; errors = null }"
        />
      </AppModal>
    </Transition>

    <!--  delete category-->
    <Transition name="fade">
      <DeleteModal
        v-if="categoryToDelete"
        title="Category"
        text="Are you sure you want to delete this category from this event?"
        :entity-name="categoryToDelete.name"
        :errors="errors"
        :processing="isSubmitting"
        @delete="handleCategoryDelete"
        @close="() => { categoryToDelete = null; errors = null }"
      />
    </Transition>
  </div>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { inject, ref } from 'vue';
import { useRoute } from 'vue-router';

import { AppButton, AppDropdown, AppDropdownItem, AppModal, AppSpinner, AppTable } from '@/components/app';

import { AdminCTA } from '@/components/app/admin';

import { CategoryForm } from '@/components/forms';
import { DeleteModal, UploadEventFileModal } from '@/components/modals';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import { useDateFormatter, useFormSubmit, useHttp } from '@/composables';

const dateFormatter = useDateFormatter();

const route = useRoute();

const event = inject('event');
const loading = inject('event.loading');

const showAddImageModal = ref(false);
const showEventDeleteImageModel = ref(false);
const imageEndpoint = ref(null);
const imageType = ref(null);
const image = ref(null);

const showAddCategoryModal = ref(false);
const categoryToDelete = ref(null);
const categoryToEdit = ref(null);

const { isSubmitting, errors } = useFormSubmit();

const handleAddCategories = async (formData) => {
  errors.value = null;
  isSubmitting.value = true;

  const response = await useHttp().post(`/events/${route.params.slug}/categories`, formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    event.value.categories.push(response.data);

    showAddCategoryModal.value = null;
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors;
  }

  isSubmitting.value = false;
};

const handleEditCategories = async (formData) => {
  errors.value = null;
  isSubmitting.value = true;

  const response = await useHttp().put(`/events/${route.params.slug}/categories/${categoryToEdit.value.id}`, formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    const index = event.value.categories.findIndex(category => category.id === categoryToEdit.value.id);

    // If the category is found, update it with the new data, preserving the ID
    if (index !== -1) {
      event.value.categories[index] = response.data;
    }
    categoryToEdit.value = null;
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors;
  }

  isSubmitting.value = false;
};

const handleCategoryDelete = async () => {
  errors.value = null;
  isSubmitting.value = true;

  const response = await useHttp().delete(`/events/${route.params.slug}/categories/${categoryToDelete.value.id}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    // Update the photos array to delete the deleted photo
    event.value.categories = event.value.categories.filter(category => category.id !== categoryToDelete.value.id);
  }

  categoryToDelete.value = null;
  isSubmitting.value = false;
};

const handleImageDelete = async () => {
  errors.value = null;
  isSubmitting.value = true;

  const response = await useHttp().delete(`/events/${route.params.slug}/${imageEndpoint.value}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    // Update the `event` to delete the deleted image
    switch (imageEndpoint.value) {
      case 'event-image':
        event.value.eventImage = null;
        break;
      case 'event-background-image':
        event.value.eventBackgroundImage = null;
        break;
      case 'sponsor-overlay-portrait':
        event.value.sponsorOverlayPortrait = null;
        break;
      case 'sponsor-overlay-landscape':
        event.value.sponsorOverlayLandscape = null;
        break;
      case 'event-overlay-portrait':
        event.value.eventOverlayPortrait = null;
        break;
      case 'event-overlay-landscape':
        event.value.eventOverlayLandscape = null;
        break;
      default:
        errors.value = 'Something went wrong. Please try again';
    }
  }

  imageEndpoint.value = null;
  imageType.value = null;
  showEventDeleteImageModel.value = false;
  isSubmitting.value = false;
};

const handleUploadImage = async (fileData) => {
  errors.value = null;
  isSubmitting.value = true;

  const data = new FormData();

  data.set('file', fileData.file);

  const response = await useHttp().post(`/events/${route.params.slug}/${imageEndpoint.value}`, data);

  if (response.statusCode === HttpStatusCode.Ok) {
    // Update the `event` to show the new uploaded image
    switch (imageEndpoint.value) {
      case 'event-image':
        event.value.eventImage = response.data.eventImage;
        break;
      case 'event-background-image':
        event.value.eventBackgroundImage = response.data.eventBackgroundImage;
        break;
      case 'sponsor-overlay-portrait':
        event.value.sponsorOverlayPortrait = response.data.sponsorOverlayPortrait;
        break;
      case 'sponsor-overlay-landscape':
        event.value.sponsorOverlayLandscape = response.data.sponsorOverlayLandscape;
        break;
      case 'event-overlay-portrait':
        event.value.eventOverlayPortrait = response.data.eventOverlayPortrait;
        break;
      case 'event-overlay-landscape':
        event.value.eventOverlayLandscape = response.data.eventOverlayLandscape;
        break;
      default:
        errors.value = 'Something Went wrong. Please try again';
    }
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors.file[0];
  }

  imageEndpoint.value = null;
  showAddImageModal.value = false;
  imageType.value = null;
  isSubmitting.value = false;
};

const handleShowImageDeleteModal = (endpoint, type) => {
  showEventDeleteImageModel.value = true;
  imageEndpoint.value = endpoint;
  imageType.value = type;
};

const handleShowUploadImage = (endpoint, type, img) => {
  showAddImageModal.value = true;
  imageEndpoint.value = endpoint;
  imageType.value = type;
  image.value = img;
};
</script>
